import { useEffect, useMemo, useRef, useState } from 'react'
import { AmigoWalkthroughVideo } from './AmigoWalkthroughVideo'

type ScanShot = { title: string; instruction: string; tip: string; overlay: string }
type QualityResult = { level: 'good' | 'warning' | 'poor'; message: string; width: number; height: number }

const scanShots: readonly ScanShot[] = [
  { title: 'Left corner', instruction: 'Stand near the entrance and frame the left corner, floor edges and both walls.', tip: 'Keep the phone level. Do not zoom.', overlay: 'Align the corner inside the guide' },
  { title: 'Opposite corner', instruction: 'Move to the opposite side and capture the other corner with overlapping surfaces.', tip: 'Include part of the same floor or wall seen in photo 1.', overlay: 'Keep shared edges visible' },
  { title: 'Full work area', instruction: 'Capture one wide view showing the complete floor, wall or service area.', tip: 'Show the boundaries of the area whenever possible.', overlay: 'Fit the full area inside the frame' },
]
const lastScanStep = scanShots.length - 1

type Props = { files: File[]; onChange: (files: File[]) => void }

async function inspectPhoto(file: File): Promise<QualityResult> {
  const bitmap = await createImageBitmap(file)
  try {
    if (bitmap.width < 900 || bitmap.height < 600) return { level: 'poor', message: 'Resolution is too low. Move closer or use the main camera.', width: bitmap.width, height: bitmap.height }
    const canvas = document.createElement('canvas')
    canvas.width = 96
    canvas.height = 72
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) return { level: 'warning', message: 'Photo captured. Quality could not be checked on this device.', width: bitmap.width, height: bitmap.height }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
    let brightness = 0
    let contrast = 0
    const luminance: number[] = []
    for (let index = 0; index < pixels.length; index += 4) {
      const value = 0.2126 * pixels[index]! + 0.7152 * pixels[index + 1]! + 0.0722 * pixels[index + 2]!
      luminance.push(value)
      brightness += value
    }
    brightness /= luminance.length
    for (const value of luminance) contrast += Math.abs(value - brightness)
    contrast /= luminance.length
    if (brightness < 45) return { level: 'poor', message: 'This photo is too dark. Turn on more lights or move toward the light.', width: bitmap.width, height: bitmap.height }
    if (brightness > 225) return { level: 'poor', message: 'This photo is overexposed. Avoid pointing directly at a bright window.', width: bitmap.width, height: bitmap.height }
    if (contrast < 14) return { level: 'warning', message: 'The image may be blurry or lacks visible edges. Retake it for a better measurement.', width: bitmap.width, height: bitmap.height }
    return { level: 'good', message: 'Photo quality looks good for visual analysis.', width: bitmap.width, height: bitmap.height }
  } finally { bitmap.close() }
}

export function AmigoCameraScan({ files, onChange }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState(Math.min(files.length, lastScanStep))
  const [isChecking, setIsChecking] = useState(false)
  const [quality, setQuality] = useState<Record<number, QualityResult>>({})
  const inputRef = useRef<HTMLInputElement>(null)
  const previews = useMemo(() => files.slice(0, 3).map((file) => ({ file, url: URL.createObjectURL(file) })), [files])
  useEffect(() => () => previews.forEach((preview) => URL.revokeObjectURL(preview.url)), [previews])

  const completed = files.length >= scanShots.length
  const safeStep = Math.max(0, Math.min(step, lastScanStep))
  const current = scanShots[safeStep]
  if (!current) return null

  function start() { setStep(Math.min(files.length, lastScanStep)); setIsOpen(true) }
  function capture(index: number) { setStep(Math.max(0, Math.min(index, lastScanStep))); window.setTimeout(() => inputRef.current?.click(), 0) }

  async function addPhoto(file: File) {
    setIsChecking(true)
    try {
      const result = await inspectPhoto(file)
      setQuality((value) => ({ ...value, [safeStep]: result }))
      if (result.level === 'poor') return
      const next = [...files]
      next[safeStep] = file
      onChange(next.slice(0, 3))
      if (safeStep < lastScanStep) setStep(safeStep + 1)
    } catch {
      setQuality((value) => ({ ...value, [safeStep]: { level: 'poor', message: 'This file could not be read as a valid photo.', width: 0, height: 0 } }))
    } finally { setIsChecking(false) }
  }

  function removePhoto(index: number) {
    onChange(files.filter((_, fileIndex) => fileIndex !== index))
    setQuality((value) => { const next = { ...value }; delete next[index]; return next })
    setStep(Math.min(index, lastScanStep))
  }

  function useVideoFrames(nextFiles: File[]) {
    onChange(nextFiles.slice(0, 3))
    setQuality({})
    setStep(lastScanStep)
  }

  const progress = Math.round((Math.min(files.length, 3) / 3) * 100)

  return <div className="amigo-camera-scan">
    <div className="camera-scan-summary"><div><strong>AMIGO Camera Scan™</strong><p>{completed ? 'Scan complete. Review each photo before generating the estimate.' : 'Capture three guided views, or record a walkthrough video.'}</p></div><button type="button" className="secondary-button" onClick={start}>{completed ? 'Review scan' : files.length ? 'Continue scan' : 'Start scan'}</button></div>
    <div className="camera-scan-progress" aria-label={`${Math.min(files.length, 3)} of 3 photos captured`}>{scanShots.map((shot, index) => <div key={shot.title} className={files[index] ? 'scan-progress-step complete' : index === safeStep ? 'scan-progress-step active' : 'scan-progress-step'}><span>{files[index] ? '✓' : index + 1}</span><small>{shot.title}</small></div>)}</div>
    {previews.length > 0 && <div className="camera-scan-thumbnails">{previews.map(({ file, url }, index) => <figure key={`${file.name}-${file.lastModified}-${index}`}><img src={url} alt={`${scanShots[index]?.title ?? 'Scan'} preview`} /><figcaption>{scanShots[index]?.title}</figcaption>{quality[index] && <p className={`scan-quality ${quality[index].level}`}>{quality[index].message}</p>}<div><button type="button" onClick={() => capture(index)}>Retake</button><button type="button" onClick={() => removePhoto(index)}>Remove</button></div></figure>)}</div>}
    <input ref={inputRef} className="camera-scan-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic" capture="environment" onChange={(event) => { const file = event.target.files?.[0]; if (file) void addPhoto(file); event.currentTarget.value = '' }} />

    {isOpen && <div className="camera-scan-overlay" role="dialog" aria-modal="true" aria-labelledby="camera-scan-title"><div className="camera-scan-dialog">
      <button type="button" className="camera-scan-close" aria-label="Close guided scan" onClick={() => setIsOpen(false)}>×</button>
      <p className="eyebrow">AMIGO Camera Scan™</p><div className="camera-scan-bar"><span style={{ width: `${progress}%` }} /></div><p className="camera-scan-count">Photo {safeStep + 1} of 3 · {progress}% complete</p>
      <div className="camera-viewfinder" aria-hidden="true"><div className="camera-grid" /><div className="camera-corners" /><span>{current.overlay}</span></div>
      <h2 id="camera-scan-title">{current.title}</h2><p>{current.instruction}</p><p className="form-note">{current.tip}</p>
      {files[safeStep] && <img className="camera-scan-current-preview" src={previews[safeStep]?.url} alt={`${current.title} captured`} />}
      {quality[safeStep] && <div className={`camera-quality-card ${quality[safeStep].level}`}><strong>{quality[safeStep].level === 'good' ? 'Looks good' : quality[safeStep].level === 'warning' ? 'Quality warning' : 'Retake needed'}</strong><span>{quality[safeStep].message}</span></div>}
      <button type="button" className="camera-capture-button" disabled={isChecking} onClick={() => capture(safeStep)}>{isChecking ? 'Checking photo quality…' : files[safeStep] ? 'Retake this photo' : 'Take photo'}</button>
      <div className="camera-scan-actions"><button type="button" className="text-button" disabled={safeStep === 0} onClick={() => setStep((value) => Math.max(0, value - 1))}>Back</button>{safeStep < lastScanStep && <button type="button" className="text-button" disabled={!files[safeStep]} onClick={() => setStep((value) => Math.min(lastScanStep, value + 1))}>Next</button>}{safeStep === lastScanStep && <button type="button" className="text-button" disabled={!completed} onClick={() => setIsOpen(false)}>Finish scan</button>}</div>
    </div></div>}

    <AmigoWalkthroughVideo onFrames={useVideoFrames} />
  </div>
}
